// NewRelic Browser Agent Helper Functions
// The agent is loaded via NPM package (@newrelic/browser-agent) in newrelic.ts

interface User {
  id?: string | number
  email?: string
  customerTypeId?: string | number
  locale?: string
}

interface MasqueradeContext {
  masquerading?: boolean
  originalUser?: string | number
}

// Helper to check if NewRelic agent is fully initialized
const isNewRelicReady = (): boolean => {
  return typeof window.newrelic?.setCustomAttribute === 'function'
}

// Set custom attributes for the current user
export function setNewRelicUser(user: User | null, context: MasqueradeContext = {}): void {
  if (!isNewRelicReady()) return

  if (!user) {
    window.newrelic!.setCustomAttribute('userId', null)
    window.newrelic!.setCustomAttribute('userEmail', null)
    window.newrelic!.setCustomAttribute('userType', null)
    window.newrelic!.setCustomAttribute('masquerading', null)
    window.newrelic!.setCustomAttribute('originalUserId', null)
    return
  }

  window.newrelic!.setCustomAttribute('userId', user.id || null)
  window.newrelic!.setCustomAttribute('userEmail', user.email || null)
  window.newrelic!.setCustomAttribute('userType', user.customerTypeId || null)
  window.newrelic!.setCustomAttribute('locale', user.locale || 'en-US')

  // Add masquerading context if provided
  if (context.masquerading) {
    window.newrelic!.setCustomAttribute('masquerading', true)
    window.newrelic!.setCustomAttribute('originalUserId', context.originalUser || null)
  } else {
    window.newrelic!.setCustomAttribute('masquerading', false)
    window.newrelic!.setCustomAttribute('originalUserId', null)
  }
}

// Track page views
export function trackPageView(pageName: string, properties: Record<string, unknown> = {}): void {
  if (!isNewRelicReady()) return

  window.newrelic!.addPageAction('page_view', {
    pageName,
    ...properties
  })
}

// Track custom events
export function trackEvent(eventName: string, properties: Record<string, unknown> = {}): void {
  if (!isNewRelicReady()) return

  window.newrelic!.addPageAction(eventName, properties)
}

// Track errors manually
export function noticeError(error: Error | string, customAttributes: Record<string, unknown> = {}): void {
  if (!isNewRelicReady()) return

  window.newrelic!.noticeError(error, customAttributes)
}

// Add custom attributes to the current page view
export function setCustomAttribute(key: string, value: string | number | boolean | null): void {
  if (!isNewRelicReady()) return

  window.newrelic!.setCustomAttribute(key, value)
}

// Track interactions (for SPA)
export function interaction(): { save: () => void; end: () => void } | null {
  if (!window.newrelic?.interaction) return null

  return window.newrelic.interaction()
}
